import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useLocale, useTranslations } from 'gt-react'
import { Bell } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useUserName } from '@/components/user-name'
import { formatDateTime } from '@/lib/intl-format'
import { orpc } from '@/lib/orpcClient'
import { notificationText } from './notification-text'

interface NotificationRow {
  id: string
  kind: string
  actorUserId: string | null
  params: Record<string, unknown>
  href: string
  readAt: Date | null
  createdAt: Date
}

const MAX_BADGE = 99

function NotificationItem({ row, onOpen }: { row: NotificationRow; onOpen: () => void }) {
  const t = useTranslations()
  const locale = useLocale()
  const actor = useUserName(row.actorUserId)
  const text = (key: string) => String(row.params[key] ?? '')
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-start gap-2 border-b px-3 py-2 text-start last:border-b-0 hover:bg-muted"
    >
      <span
        className={`mt-1.5 size-2 shrink-0 rounded-full ${row.readAt ? 'bg-transparent' : 'bg-primary'}`}
      />
      <span className="min-w-0 flex-1 space-y-0.5">
        <span className="block text-sm">
          {/* De tekst komt uit de soort en zijn gegevens, in de taal van de lezer. */}
          {notificationText(
            row,
            { actor, board: text('board'), label: text('label'), reason: text('reason') },
            t,
          )}
        </span>
        {text('snippet') && (
          <span className="block truncate text-xs text-muted-foreground">{text('snippet')}</span>
        )}
        <span className="block text-xs text-muted-foreground">
          {formatDateTime(row.createdAt, locale)}
        </span>
      </span>
    </button>
  )
}

/** De bel in de bovenbalk: hoeveel ongelezen meldingen je hebt, en de lijst erachter. */
export function NotificationBell() {
  const t = useTranslations()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const countQ = useQuery(orpc.notifications.unreadCount.queryOptions({}))
  const listQ = useQuery({
    ...orpc.notifications.list.queryOptions({ input: {} }),
    enabled: open,
  })
  const markRead = useMutation(orpc.notifications.markRead.mutationOptions())
  const markAllRead = useMutation(orpc.notifications.markAllRead.mutationOptions())
  const unread = countQ.data?.count ?? 0
  const rows = (listQ.data ?? []) as NotificationRow[]

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={
            unread > 0 ? t('notifications.unread', { n: String(unread) }) : t('notifications.title')
          }
        >
          <Bell className="size-4" />
          {unread > 0 && (
            <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-xs font-medium text-destructive-foreground">
              {unread > MAX_BADGE ? `${MAX_BADGE}+` : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-semibold">{t('notifications.title')}</span>
          <Button
            variant="ghost"
            size="sm"
            disabled={unread === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate(undefined)}
          >
            {t('notifications.markAllRead')}
          </Button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {rows.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {listQ.isLoading ? '...' : t('notifications.empty')}
            </p>
          ) : (
            rows.map((row) => (
              <NotificationItem
                key={row.id}
                row={row}
                onOpen={() => {
                  if (!row.readAt) markRead.mutate({ id: row.id })
                  setOpen(false)
                  void navigate({ href: row.href })
                }}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
